'use client';

import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../app/hooks';
import type { RootState } from '../../app/store';
import BarchartComp from '../../components/barChart/BarChart';
import Button from '../../components/button/Button';
import { EarningInfoBox } from '../../components/earningInfoBox/EarningInfoBox';
import HistoryRouting from '../../components/HistoryRouting';
import type { FilterByDateWithFlag } from '../../components/lineChart/LineChart';
import Spinner from '../../components/spinner/Spinner';
import {
  GetDashboardEarningInfo,
  GetDashboardInfo,
  GetGraphPartnerPayouts,
} from '../../features/partnerSlice';
import {
  GetGraphCampaignData,
  GetGraphMemberEarnings,
  GetGraphPartnerEarnings,
  setGraphCampaignData,
  setGraphMemberEarnings,
  setGraphPartnerEarnings,
} from '../../features/reportingSlice';
import { useEndpointLoading } from '../../hooks/useApiLoading';
import { api } from '../../utils/api/apiClient';
import { useLocation } from 'react-router';
import { formatDateString } from '../../assets/helper';

const PartnerErnings = () => {
  const location = useLocation();
  const incomingTab = location.state?.activeTab || '1';
  const [activeTab, setActiveTab] = useState<'1' | '2' | '3'>(incomingTab);
  const isEarningsLoading = useEndpointLoading('/usr/p/c/d', 'POST');
  const [customDate, setCustomDate] = useState<FilterByDateWithFlag>({
    startDate: '',
    endDate: '',
    typeName: '',
  });
  const {
    dashboardInfo,
    GraphPartnerPayouts,
    dashboardEarningInfo,
    loadingStates: PartnerLoadingState,
  } = useSelector((state: RootState) => state.partner);
  const {
    graphPartnerEarnings,
    graphMemberEarnings,
    campaignData,
    loadingStates,
  } = useSelector((state: RootState) => state.reporting);
  const dispatch = useAppDispatch();
  const [partnerData, setPartnerData] = useState<any>(null);

  useEffect(() => {
    if (!dashboardInfo) {
      dispatch(GetDashboardInfo());
    }
  }, [dashboardInfo, dispatch]);
  useEffect(() => {
    if (!dashboardEarningInfo) {
      dispatch(GetDashboardEarningInfo());
    }
  }, [dashboardEarningInfo, dispatch]);

  useEffect(() => {
    const { typeName, ...date } = customDate;
    if (activeTab !== '3') {
      if (typeName === 'PartnerEarnings') {
        dispatch(GetGraphPartnerEarnings(date));
      } else if (!graphPartnerEarnings && activeTab === '1') {
        dispatch(GetGraphPartnerEarnings());
      }

      if (typeName === 'PartnerPayouts') {
        dispatch(GetGraphPartnerPayouts(date));
      } else if (!GraphPartnerPayouts && activeTab === '1') {
        dispatch(GetGraphPartnerPayouts());
      }
      if (typeName === 'MemberEarnings') {
        dispatch(GetGraphMemberEarnings(date));
      } else if (!graphMemberEarnings && activeTab === '2') {
        dispatch(GetGraphMemberEarnings());
      }
    }
    if (activeTab === '3' && !campaignData) {
      dispatch(GetGraphCampaignData());
    }

    if (typeName) {
      setCustomDate(prev => ({ ...prev, typeName: '' }));
    }
  }, [
    activeTab,
    customDate,
    dispatch,
    graphPartnerEarnings,
    graphMemberEarnings,
    GraphPartnerPayouts,
    campaignData,
  ]);

  useEffect(() => {
    return () => {
      dispatch(setGraphMemberEarnings(null));
      dispatch(setGraphPartnerEarnings(null));
      dispatch(setGraphPartnerEarnings(null));
      dispatch(setGraphCampaignData(null));
    };
  }, [dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.post('/usr/p/c/d');
        setPartnerData(response);
      } catch (error) {
        console.error('API Error:', error);
      }
    };

    fetchData();
  }, []);

  // const partnerDataTemp = {
  //   totalEarned: '$80.00',
  //   available: '$24.00',
  //   pending: '$55.50',
  //   receivePayout: '',
  //   payoutDate: '05.15.2025',
  // };
  // Determine which boxes to show based on the conditions
  const shouldShowBoxB = partnerData?.receivePayout === '';
  const shouldShowBoxC = !shouldShowBoxB;

  return (
    <div className='mb-8'>
      {/* Toggle Button */}
      <HistoryRouting history='Reporting/Earnings Data' />
      <div className='space-y-10'>
        {/* A - B - C - D Boxes */}
        {isEarningsLoading ? (
          <div className='ml-12 my-4'>
            <Spinner />
          </div>
        ) : activeTab === '1' ? (
          <div className='grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3'>
            {/* A - Life time program payouts (always shown) */}
            <EarningInfoBox
              title='Lifetime Partner Payouts'
              value={dashboardEarningInfo?.partnerPayouts?.value || '$0.00'}
            />

            {shouldShowBoxB && (
              <EarningInfoBox
                title='Next Payout'
                value={partnerData?.available || '$0.00'}
                // badge='NEW'
                extraContent={
                  <p className='text-sm text-gray-600'>
                    Date: {formatDateString(partnerData?.payoutDate) ?? 'N/A'}
                  </p>
                }
              />
            )}

            {shouldShowBoxC && (
              <EarningInfoBox
                title='Confirmed Partner Earnings'
                value={
                  dashboardEarningInfo?.partnerConfirmedEarnings?.value ||
                  '$0.00'
                }
                tooltip='You need to reach $25 to qualify for payouts'
              />
            )}

            <EarningInfoBox
              title='Pending Partner Earnings'
              value={
                dashboardEarningInfo?.partnerPendingEarnings?.value || '$0.00'
              }
              tooltip='Earnings that are still being processed'
            />
          </div>
        ) : (
          <div className='grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3'>
            <EarningInfoBox
              title='Lifetime Member Payouts'
              value={dashboardEarningInfo?.userPayouts?.value || '$0.00'}
            />
            <EarningInfoBox
              title='Confirmed Member Cashback'
              value={
                dashboardEarningInfo?.usersConfirmedEarnings?.value || '$0.00'
              }
            />
            <EarningInfoBox
              title='Pending Member Cashback'
              value={
                dashboardEarningInfo?.usersPendingEarnings?.value || '$0.00'
              }
            />
          </div>
        )}
        <div className='border border-gray-200 primary-shadow px-7 pt-7 pb-5'>
          <div className='flex gap-10'>
            <Button
              className={` ${
                activeTab === '1'
                  ? 'bg-[#00C389] text-white'
                  : 'text-black bg-white'
              }    w-full`}
              onClick={() => setActiveTab('1')}
            >
              Partner Cashback
            </Button>
            <Button
              className={` ${
                activeTab === '2'
                  ? 'bg-[#00C389] text-white'
                  : 'text-black bg-white'
              }    w-full`}
              onClick={() => setActiveTab('2')}
            >
              Member Cashback
            </Button>
           {/* <Button
              className={` ${
                activeTab === '3'
                  ? 'bg-[#00C389] text-white'
                  : 'text-black bg-white'
              }    w-full`}
              onClick={() => setActiveTab('3')}
            >
              Program Booster Earnings
            </Button>*/}
          </div>
          {/* Display Charts or List View */}
          <div className='grid grid-cols-1 '>
            {activeTab === '1' && (
              <div className='space-y-6 my-6'>
                <BarchartComp
                  data={graphPartnerEarnings}
                  title='Partner Earnings'
                  dataKey1='pending'
                  name1='pending'
                  dataKey2='confirmed'
                  name2='confirmed'
                  dataKey3='rejected'
                  name3='rejected/cancelled/refunded'
                  chartName='PartnerEarnings'
                  setCustomDate={setCustomDate}
                  dollar={true}
                  isFiltering={true}
                  description='Monthly Partner Earnings'
                  isLoading={loadingStates['GetGraphPartnerEarnings']}
                  showExport
                  isShadowBox
                  isStack
                />
                <BarchartComp
                  title='Partner Payouts'
                  data={GraphPartnerPayouts}
                  dataKey1='amount'
                  name1='Partner Payouts'
                  chartName='PartnerPayouts'
                  dollar={true}
                  isFiltering={true}
                  isOneColumn={true}
                  description='Monthly Payouts'
                  setCustomDate={setCustomDate}
                  isLoading={PartnerLoadingState['GetGraphPartnerPayouts']}
                  showExport
                  isShadowBox
                />
              </div>
            )}
            {activeTab === '2' && (
              <BarchartComp
                data={graphMemberEarnings}
                dataKey1='pending'
                name1='pending'
                dataKey2='confirmed'
                name2='confirmed'
                dataKey3='rejected'
                name3='rejected'
                chartName='MemberEarnings'
                setCustomDate={setCustomDate}
                dollar={true}
                isFiltering={true}
                description='Monthly Active Members Earnings'
                isLoading={loadingStates['GetGraphMemberEarnings']}
                showExport
                isStack
              />
            )}
            {activeTab === '3' && (
              <BarchartComp
                data={campaignData}
                dataKey1='qualifiedUsers'
                name1='Qualified Sign-Ups'
                dataKey2='notQualifiedUsers'
                name2='Unqualified Sign-ups'
                chartName='MemberEarnings'
                setCustomDate={setCustomDate}
                description='Campaign Rewards'
                isLoading={loadingStates['GetGraphCampaignData']}
                showExport
                isStack
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerErnings;
